import {
  ApplicationConfig,
  importProvidersFrom,
  isDevMode,
  provideZoneChangeDetection,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  provideRouter,
  withHashLocation,
  withViewTransitions,
} from '@angular/router';
import { routes } from './app.routes';
import { provideClientHydration } from '@angular/platform-browser';
import { provideToastr } from 'ngx-toastr';
import { MetaReducer, provideStore } from '@ngrx/store';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import {
  HTTP_INTERCEPTORS,
  HttpBackend,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import { HttpLoaderFactory } from './shared/utils/loader-factory';
import { environment } from '../environments/environment';
import { localStorageSync } from 'ngrx-store-localstorage';
import {
  DefaultDataServiceConfig,
  EntityDataModuleConfig,
  EntityMetadataMap,
  provideEntityData,
} from '@ngrx/data';
import {
  AppEffects,
  AppUpdateService,
  reducer,
  StorageService,
  SyncStorageService,
} from '@kimbocare/core';
import { ErrorInterceptor, TokenInterceptor } from '@kimbocare/auth';
import { provideEffects } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideServiceWorker } from '@angular/service-worker';
import {
  MAT_DATE_LOCALE,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { AuthModule } from '@kimbocare/auth';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideCacheableAnimationLoader,
  provideLottieOptions,
} from 'ngx-lottie';
import player from 'lottie-web';

export function localStorageSyncReducer(reducer: any): any {
  return localStorageSync({
    keys: ['auth'], // Specify the state slices to persist
    rehydrate: true, // Automatically rehydrate the state from localStorage
  })(reducer);
}

export const metaReducers: MetaReducer<any>[] = [localStorageSyncReducer];

const entityMetadata: EntityMetadataMap = {
  Contact: {
    entityDispatcherOptions: {
      optimisticUpdate: false,
      optimisticAdd: false,
    },
    selectId: (entity) => entity.uuid,
  },
  HealthPackage: {
    entityDispatcherOptions: {
      optimisticUpdate: true,
    },
    selectId: (entity) => entity.uuid,
  },
};

const defaultDataServiceConfig: DefaultDataServiceConfig = {
  root: `${environment.basePath}/${environment.apiProvider}`,
  timeout: 3000, // request timeout
};

const entityConfig: EntityDataModuleConfig = {
  entityMetadata,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withViewTransitions(), withHashLocation()),
    provideAnimations(),
    provideClientHydration(),
    provideToastr(),
    importProvidersFrom(AuthModule.forRoot()),
    provideStore(
      { appState: reducer },
      {
        metaReducers: metaReducers,
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: false,
        },
      },
    ),
    provideEffects([AppEffects]),
    provideEntityData(entityConfig),
    importProvidersFrom(
      StoreDevtoolsModule.instrument({ logOnly: !isDevMode() }),
    ),

    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi(),
      withJsonpSupport(),
    ),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    provideLottieOptions({ player: () => player }),
    provideCacheableAnimationLoader(),
    provideNativeDateAdapter(),
    {
      provide: DefaultDataServiceConfig,
      useValue: defaultDataServiceConfig,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    { provide: 'env', useValue: environment },
    StorageService,
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker,
      registrationStrategy: 'registerWhenStable:30000',
    }),

    provideAppInitializer(() => {
      const initializerFn = (
        (ds: SyncStorageService) => () =>
          ds.syncAll()
      )(inject(SyncStorageService));
      return initializerFn();
    }),
    
    AppUpdateService,
    provideAppInitializer(() => {
      const initializerFn = (
        (ds: AppUpdateService) => () =>
          ds.init()
      )(inject(AppUpdateService));
      return initializerFn();
    }),
  ],
};
